
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Home, Shield, ArrowRight, Check, Tag, Star, MapPin } from "lucide-react";
import { useSessionContext } from "@supabase/auth-helpers-react";

export function PropertyHero() {
  const navigate = useNavigate();
  const { session } = useSessionContext();

  return (
    <div className="relative">
      <div className="relative bg-gradient-to-r from-purple-900 to-indigo-800 text-white py-16 md:py-24 overflow-hidden">
        {/* Background image with overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/lovable-uploads/8606a715-f85b-4993-bcf3-59a3addef053.png" 
            alt="Luxury modern villa with pool"
            className="w-full h-full object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/70 to-indigo-800/70 mix-blend-multiply"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <div className="inline-block bg-green-500/90 backdrop-blur-sm px-4 py-1 rounded-full text-sm font-medium mb-2">
                <span className="flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Limited Time Offer: 15% Off Selected Properties
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
                Exclusive Properties for Sale in Premium Locations
              </h1>
              <p className="text-lg text-purple-100">
                Secure your dream home today with just 20% down payment. Our properties offer exceptional investment opportunities with high returns.
              </p>
              
              <div className="grid grid-cols-2 gap-4 my-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex items-center text-amber-300 mb-2">
                    <Star className="h-5 w-5 fill-amber-300" />
                    <Star className="h-5 w-5 fill-amber-300" />
                    <Star className="h-5 w-5 fill-amber-300" />
                    <Star className="h-5 w-5 fill-amber-300" />
                    <Star className="h-5 w-5 fill-amber-300" />
                  </div>
                  <p className="text-sm font-medium">Premium properties in high-demand locations with guaranteed value appreciation</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-400 mb-2">
                    <MapPin className="h-5 w-5" />
                    <span className="font-semibold">Prime Locations</span>
                  </div>
                  <p className="text-sm">Exclusive neighborhoods with high investment potential and quality amenities</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="bg-green-500 p-1 rounded-full">
                    <Check className="h-3 w-3" />
                  </div>
                  <span>100% verified properties with clean documentation</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-green-500 p-1 rounded-full">
                    <Check className="h-3 w-3" />
                  </div>
                  <span>Instant pre-approval for property financing</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-green-500 p-1 rounded-full">
                    <Check className="h-3 w-3" />
                  </div>
                  <span>Use as collateral for up to 70% of property value</span>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl transition-all"
                  onClick={() => {
                    const element = document.getElementById('property-listings');
                    element?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <Tag className="mr-2 h-4 w-4" />
                  View Hot Deals
                </Button>
                {session ? (
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="border-white text-white hover:bg-purple-800"
                    onClick={() => navigate('/borrower/properties')}
                  >
                    My Property Portfolio
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="border-white text-white hover:bg-purple-800"
                    onClick={() => navigate('/register')}
                  >
                    Register for Special Offers
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            
            <div className="rounded-lg overflow-hidden shadow-xl hidden lg:block">
              <div className="relative">
                <img 
                  src="/lovable-uploads/8606a715-f85b-4993-bcf3-59a3addef053.png" 
                  alt="Modern luxury villa with swimming pool" 
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <div className="text-white">
                    <div className="text-green-400 font-bold text-2xl mb-1">GHS 950,000</div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-white/80" />
                      <span className="text-white/80">East Legon, Accra</span>
                    </div>
                  </div>
                </div>
                <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                  Featured
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-b from-transparent to-white"></div>
    </div>
  );
}
